import { Box, Grid, Typography } from "@mui/material";
import {
  ChartsGrid,
  ChartsLegend,
  ChartsXAxis,
  ChartsYAxis,
  LineChartPro,
  lineElementClasses,
  LinePlot,
  markElementClasses,
  MarkPlot,
} from "@mui/x-charts-pro";
import { format } from "date-fns";
import { CustomItemTooltip } from "../../../../../ResuableChartTooltip/CustomItemTooltip";
import { useSelector } from "react-redux";

const PerformanceDashboardRealUsersExperience = () => {
  const { exequteQueryContentQuery5, exequteQueryContentQuery6, selectedPage } =
    useSelector((state: any) => state.pageSpeedPerformance);

  // Helper function to sort dates
  const sortDates = (dates: string[]) => {
    return dates.sort((a, b) => {
      const [dayA, monthA, yearA] = a.split("-").map(Number);
      const [dayB, monthB, yearB] = b.split("-").map(Number);

      const dateA = new Date(yearA, monthA - 1, dayA);
      const dateB = new Date(yearB, monthB - 1, dayB);

      return dateA.getTime() - dateB.getTime();
    });
  };

  const GraphLCP =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      y: parseFloat((item?.Avg_Real_Users_Experience_LCP || 0).toFixed(2)),
    })) || [];

  const GraphPageSpecificLCP =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"),
        y: parseFloat((item?.Real_Users_Experience_LCP || 0).toFixed(2)),
      })) || [];

  const GraphLCPTarget =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      z: parseFloat((item?.lcp_target || 0).toFixed(2)),
    })) || [];

  const GraphPageSpecificLCPTarget =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"),
        z: parseFloat((item?.lcp_target || 0).toFixed(2)),
      })) || [];

  const GraphLCPAchievement =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      s: parseFloat((item?.achievement_Real_Users_lcp || 0).toFixed(2)),
    })) || [];

  const GraphLCPAchievementPageSpecific =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"),
        s: parseFloat((item?.achievement_Real_Users_lcp || 0).toFixed(2)),
      })) || [];

  const uniqueDatesLCP = sortDates(
    Array.from(
      new Set([
        ...GraphLCP.map((item: any) => item.x),
        ...GraphLCPTarget.map((item: any) => item.x),
        ...GraphLCPAchievement.map((item: any) => item.x),
      ])
    )
  );

  const uniqueDatesPageSpecificLCP = sortDates(
    Array.from(
      new Set([
        ...GraphPageSpecificLCP.map((item: any) => item.x),
        ...GraphPageSpecificLCPTarget.map((item: any) => item.x),
        ...GraphLCPAchievement.map((item: any) => item.x),
        ...GraphLCPAchievementPageSpecific.map((item: any) => item.x),
      ])
    )
  );

  const combinedDatasetLCP = (
    selectedPage
      ? uniqueDatesPageSpecificLCP.map((date) => {
          const data = GraphPageSpecificLCP.find(
            (item: any) => item.x === date
          );
          const target = GraphPageSpecificLCPTarget.find(
            (item: any) => item.x === date
          );
          const achievement = GraphLCPAchievementPageSpecific.find(
            (item: any) => item.x === date
          );

          return {
            x: date,
            y: data?.y ?? null,
            z: target?.z ?? null,
            s: achievement?.s ?? null,
          };
        })
      : uniqueDatesLCP.map((date) => {
          const data = GraphLCP.find((item: any) => item.x === date);
          const target = GraphLCPTarget.find((item: any) => item.x === date);
          const achievement = GraphLCPAchievement.find(
            (item: any) => item.x === date
          );

          return {
            x: date,
            y: data?.y ?? null,
            z: target?.z ?? null,
            s: achievement?.s ?? null,
          };
        })
  ).filter((item) => item.y !== null);

  const GraphINP =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      y: parseFloat((item?.Avg_Real_Users_Experience_INP || 0).toFixed(2)),
    })) || [];

  const GraphPageSpecificINP =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"),
        y: parseFloat((item?.Real_Users_Experience_INP || 0).toFixed(2)),
      })) || [];

  const GraphINPTarget =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      z: parseFloat((item?.inp_target || 0).toFixed(2)),
    })) || [];

  const GraphINPAchievement =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      s: parseFloat(((item?.achievement_Real_Users_inp || 0) * 100).toFixed(2)),
    })) || [];

  const GraphINPAchievementPageSpecific =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"),
        s: parseFloat(
          ((item?.achievement_Real_Users_inp || 0) * 100).toFixed(2)
        ),
      })) || [];

  const GraphPageSpecificINPTarget =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"),
        z: parseFloat((item?.inp_target || 0).toFixed(2)),
      })) || [];

  const uniqueDatesINP = sortDates(
    Array.from(
      new Set([
        ...GraphINP.map((item: any) => item.x),
        ...GraphINPTarget.map((item: any) => item.x),
        ...GraphINPAchievement.map((item: any) => item.x),
      ])
    )
  );

  const uniqueDatesPageSpecificINP = sortDates(
    Array.from(
      new Set([
        ...GraphPageSpecificINP.map((item: any) => item.x),
        ...GraphPageSpecificINPTarget.map((item: any) => item.x),
        ...GraphINPAchievementPageSpecific.map((item: any) => item.x),
      ])
    )
  );

  const combinedDatasetINP = selectedPage
    ? uniqueDatesPageSpecificINP.map((date) => {
        const data = GraphPageSpecificINP.find((item: any) => item.x === date);
        const target = GraphPageSpecificINPTarget.find(
          (item: any) => item.x === date
        );
        const achievement = GraphINPAchievementPageSpecific.find(
          (item: any) => item.x === date
        );
        return {
          x: date,
          y: data?.y ?? null,
          z: target?.z ?? null,
          s: achievement?.s ?? null,
        };
      })
    : uniqueDatesINP
        .map((date) => {
          const data = GraphINP.find((item: any) => item.x === date);
          const target = GraphINPTarget.find((item: any) => item.x === date);
          const achievement = GraphINPAchievement.find(
            (item: any) => item.x === date
          );
          return {
            x: date,
            y: data?.y ?? null,
            z: target?.z ?? null,
            s: achievement?.s ?? null,
          };
        })
        .filter((item) => item.y !== null);

  const GraphCLS =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      y: parseFloat((item?.Avg_Real_Users_Experience_CLS || 0).toFixed(2)),
    })) || [];

  const GraphPageSpecificCLS =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"),
        y: parseFloat((item?.Real_Users_Experience_CLS || 0).toFixed(2)),
      })) || [];

  const GraphCLSTarget =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      z: parseFloat((item?.cls_target || 0).toFixed(2)),
    })) || [];

  const GraphPageSpecificCLSTarget =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"),
        z: parseFloat((item?.cls_target || 0).toFixed(2)),
      })) || [];

  const GraphCLSAchievement =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      s: parseFloat((item?.achievement_Real_Users_cls || 0).toFixed(2)),
    })) || [];

  const GraphCLSAchievementPageSpecific =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"),
        s: parseFloat((item?.achievement_Real_Users_cls || 0).toFixed(2)),
      })) || [];

  const uniqueDatesCLS = sortDates(
    Array.from(
      new Set([
        ...GraphCLS.map((item: any) => item.x),
        ...GraphCLSTarget.map((item: any) => item.x),
        ...GraphCLSAchievement.map((item: any) => item.x),
      ])
    )
  );

  const uniqueDatesPageSpecificCLS = sortDates(
    Array.from(
      new Set([
        ...GraphPageSpecificCLS.map((item: any) => item.x),
        ...GraphPageSpecificCLSTarget.map((item: any) => item.x),
        ...GraphCLSAchievementPageSpecific.map((item: any) => item.x),
      ])
    )
  );

  const combinedDatasetCLS = selectedPage
    ? uniqueDatesPageSpecificCLS.map((date) => {
        const data = GraphPageSpecificCLS.find((item: any) => item.x === date);
        const target = GraphPageSpecificCLSTarget.find(
          (item: any) => item.x === date
        );
        const achievement = GraphCLSAchievementPageSpecific.find(
          (item: any) => item.x === date
        );
        return {
          x: date,
          y: data?.y ?? null,
          z: target?.z ?? null,
          s: achievement?.s ?? null,
        };
      })
    : uniqueDatesCLS
        .map((date) => {
          const data = GraphCLS.find((item: any) => item.x === date);
          const target = GraphCLSTarget.find((item: any) => item.x === date);
          const achievement = GraphCLSAchievement.find(
            (item: any) => item.x === date
          );
          return {
            x: date,
            y: data?.y ?? null,
            z: target?.z ?? null,
            s: achievement?.s ?? null,
          };
        })
        .filter((item) => item.y !== null);

  const GraphFCP =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      y: parseFloat((item?.Avg_Real_Users_Experience_FCP || 0).toFixed(2)),
    })) || [];

  const GraphPageSpecificFCP =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"),
        y: parseFloat((item?.Real_Users_Experience_FCP || 0).toFixed(2)),
      })) || [];

  const GraphFCPTarget =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      z: parseFloat((item?.fcp_target || 0).toFixed(2)),
    })) || [];

  const GraphPageSpecificFCPTarget =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"),
        z: parseFloat((item?.fcp_target || 0).toFixed(2)),
      })) || [];

  const GraphFCPAchievement =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      s: parseFloat((item?.achievement_Real_Users_fcp || 0).toFixed(2)),
    })) || [];

  const GraphFCPAchievementPageSpecific =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"),
        s: parseFloat((item?.achievement_Real_Users_fcp || 0).toFixed(2)),
      })) || [];

  const uniqueDatesFCP = sortDates(
    Array.from(
      new Set([
        ...GraphFCP.map((item: any) => item.x),
        ...GraphFCPTarget.map((item: any) => item.x),
        ...GraphFCPAchievement.map((item: any) => item.x),
      ])
    )
  );

  const uniqueDatesPageSpecificFCP = sortDates(
    Array.from(
      new Set([
        ...GraphPageSpecificFCP.map((item: any) => item.x),
        ...GraphPageSpecificFCPTarget.map((item: any) => item.x),
        ...GraphFCPAchievementPageSpecific.map((item: any) => item.x),
      ])
    )
  );

  const combinedDatasetFCP = selectedPage
    ? uniqueDatesPageSpecificFCP.map((date) => {
        const data = GraphPageSpecificFCP.find((item: any) => item.x === date);
        const target = GraphPageSpecificFCPTarget.find(
          (item: any) => item.x === date
        );
        const achievement = GraphFCPAchievementPageSpecific.find(
          (item: any) => item.x === date
        );
        return {
          x: date,
          y: data?.y || 0,
          z: target?.z || 0,
          s: achievement?.s || 0,
        };
      })
    : uniqueDatesFCP
        .map((date) => {
          const data = GraphFCP.find((item: any) => item.x === date);
          const target = GraphFCPTarget.find((item: any) => item.x === date);
          const achievement = GraphFCPAchievement.find(
            (item: any) => item.x === date
          );
          return {
            x: date,
            y: data?.y || 0,
            z: target?.z || 0,
            s: achievement?.s || 0,
          };
        })
        .filter((item) => item.y !== null);

  const GraphTTFB =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      y: parseFloat((item?.Avg_Real_Users_Experience_TTFB || 0).toFixed(2)),
    })) || [];

  const GraphPageSpecificTTFB =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"),
        y: parseFloat((item?.Real_Users_Experience_TTFB || 0).toFixed(2)),
      })) || [];

  const GraphTTFBTarget =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      z: parseFloat((item?.ttfb_target || 0).toFixed(2)),
    })) || [];

  const GraphPageSpecificTTFBTarget =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"),
        z: parseFloat((item?.ttfb_target || 0).toFixed(2)),
      })) || [];

  const GraphTTFBAchievement =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      s: parseFloat((item?.achievement_Real_Users_ttfb || 0).toFixed(2)),
    })) || [];

  const GraphTTFBAchievementPageSpecific =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"),
        s: parseFloat((item?.achievement_Real_Users_ttfb || 0).toFixed(2)),
      })) || [];

  const uniqueDatesTTFB = sortDates(
    Array.from(
      new Set([
        ...GraphTTFB.map((item: any) => item.x),
        ...GraphTTFBTarget.map((item: any) => item.x),
        ...GraphTTFBAchievement.map((item: any) => item.x),
      ])
    )
  );

  const uniqueDatesPageSpecificTTFB = sortDates(
    Array.from(
      new Set([
        ...GraphPageSpecificTTFB.map((item: any) => item.x),
        ...GraphPageSpecificTTFBTarget.map((item: any) => item.x),
        ...GraphTTFBAchievementPageSpecific.map((item: any) => item.x),
      ])
    )
  );

  const combinedDatasetTTFB = selectedPage
    ? uniqueDatesPageSpecificTTFB.map((date) => {
        const data = GraphPageSpecificTTFB.find((item: any) => item.x === date);
        const target = GraphPageSpecificTTFBTarget.find(
          (item: any) => item.x === date
        );
        const achievement = GraphTTFBAchievementPageSpecific.find(
          (item: any) => item.x === date
        );
        return {
          x: date,
          y: data?.y ?? null,
          z: target?.z ?? null,
          s: achievement?.s ?? null,
        };
      })
    : uniqueDatesTTFB
        .map((date) => {
          const data = GraphTTFB.find((item: any) => item.x === date);
          const target = GraphTTFBTarget.find((item: any) => item.x === date);
          const achievement = GraphTTFBAchievement.find(
            (item: any) => item.x === date
          );
          return {
            x: date,
            y: data?.y ?? null,
            z: target?.z ?? null,
            s: achievement?.s ?? null,
          };
        })
        .filter((item) => item.y !== null);

  return (
    <>
      <Grid container spacing={3} mb={2} className="ratedTableWrap">
        <Grid item xs={6}>
          <Typography sx={{ fontSize: "18px", color: "#000000" }}>
            LCP (Largest Contentful Paint)
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#00000099" }}>
            in Seconds
          </Typography>
          <LineChartPro
            dataset={combinedDatasetLCP}
            series={[
              {
                type: "line",
                dataKey: "y",
                color: "#6200EE",
                curve: "linear",
              },
              {
                type: "line",
                dataKey: "z",
                color: "#FF9800",
                curve: "linear",
              },
            ]}
            xAxis={[{ scaleType: "point", dataKey: "x", disableLine: true }]}
            yAxis={[
              {
                disableLine: true,
                disableTicks: true,
                min: 0,
                position: "right",
              },
            ]}
            height={350}
            margin={{ left: 32, right: 42, top: 25, bottom: 30 }}
            sx={{
              [`& .${lineElementClasses.root}`]: {
                strokeWidth: 2,
              },
              [`& .${markElementClasses.root}`]: {
                scale: "0.6",
                strokeWidth: 2,
                "&:hover": {
                  scale: "1.2",
                  strokeWidth: 5,
                  opacity: "0.6",
                },
              },
            }}
            leftAxis={null}
            rightAxis={{}}
          >
            <ChartsGrid horizontal />
            <CustomItemTooltip />
            <ChartsXAxis />
            <ChartsYAxis />
            <LinePlot />
            <MarkPlot />
            <ChartsLegend direction="row" />
          </LineChartPro>
          <Grid
            container
            spacing={0}
            direction="row"
            mb={2}
            pt={2}
            className="ratedTableWrap"
            sx={{ pl: 6 }}
          >
            <Grid item xs={4}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 3,
                    backgroundColor: "#6200EE",
                    mr: 1,
                  }}
                ></Box>
                <Typography sx={{ color: "#00000099", fontSize: "12px" }}>
                  Real Users Experience (LCP)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 3,
                    backgroundColor: "#FF9800",
                    mr: 1,
                  }}
                ></Box>
                <Typography sx={{ color: "#00000099", fontSize: "12px" }}>
                  LCP Target
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}></Grid>
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <Typography sx={{ fontSize: "18px", color: "#000000" }}>
            INP (Interaction to Next Paint)
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#00000099" }}>
            in Milliseconds
          </Typography>
          <LineChartPro
            dataset={combinedDatasetINP}
            series={[
              {
                type: "line",
                dataKey: "y",
                color: "#6200EE",
                curve: "linear",
              },
              {
                type: "line",
                dataKey: "z",
                color: "#FF9800",
                curve: "linear",
              },
            ]}
            xAxis={[{ scaleType: "point", dataKey: "x", disableLine: true }]}
            yAxis={[
              {
                disableLine: true,
                disableTicks: true,
                position: "right",
              },
            ]}
            height={350}
            margin={{ left: 32, right: 42, top: 25, bottom: 30 }}
            sx={{
              [`& .${lineElementClasses.root}`]: {
                strokeWidth: 2,
              },
              [`& .${markElementClasses.root}`]: {
                scale: "0.6",
                strokeWidth: 2,
                "&:hover": {
                  scale: "1.2",
                  strokeWidth: 5,
                  opacity: "0.6",
                },
              },
            }}
            leftAxis={null}
            rightAxis={{}}
          >
            <ChartsGrid horizontal />
            <CustomItemTooltip />
            <ChartsXAxis />
            <ChartsYAxis />
            <LinePlot />
            <MarkPlot />
            <ChartsLegend direction="row" />
          </LineChartPro>
          <Grid
            container
            spacing={0}
            direction="row"
            mb={2}
            pt={2}
            className="ratedTableWrap"
            sx={{ pl: 6 }}
          >
            <Grid item xs={4}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 3,
                    backgroundColor: "#6200EE",
                    mr: 1,
                  }}
                ></Box>
                <Typography sx={{ color: "#00000099", fontSize: "12px" }}>
                  Real Users Experience (INP)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 3,
                    backgroundColor: "#FF9800",
                    mr: 1,
                  }}
                ></Box>
                <Typography sx={{ color: "#00000099", fontSize: "12px" }}>
                  INP Target
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}></Grid>
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <Typography sx={{ fontSize: "18px", color: "#000000" }}>
            CLS (Cumulative Layout Shift)
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#00000099" }}>
            in Milliseconds
          </Typography>
          <LineChartPro
            dataset={combinedDatasetCLS}
            series={[
              {
                type: "line",
                dataKey: "y",
                color: "#6200EE",
                curve: "linear",
              },
              {
                type: "line",
                dataKey: "z",
                color: "#FF9800",
                curve: "linear",
              },
            ]}
            xAxis={[{ scaleType: "point", dataKey: "x", disableLine: true }]}
            yAxis={[
              {
                disableLine: true,
                disableTicks: true,
                min: 0,
                max: 2,
                position: "right",
              },
            ]}
            height={350}
            margin={{ left: 32, right: 42, top: 25, bottom: 30 }}
            sx={{
              [`& .${lineElementClasses.root}`]: {
                strokeWidth: 2,
              },
              [`& .${markElementClasses.root}`]: {
                scale: "0.6",
                strokeWidth: 2,
                "&:hover": {
                  scale: "1.2",
                  strokeWidth: 5,
                  opacity: "0.6",
                },
              },
            }}
            leftAxis={null}
            rightAxis={{}}
          >
            <ChartsGrid horizontal />
            <CustomItemTooltip />
            <ChartsXAxis />
            <ChartsYAxis />
            <LinePlot />
            <MarkPlot />
            <ChartsLegend direction="row" />
          </LineChartPro>
          <Grid
            container
            spacing={0}
            direction="row"
            mb={2}
            pt={2}
            className="ratedTableWrap"
            sx={{ pl: 6 }}
          >
            <Grid item xs={4}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 3,
                    backgroundColor: "#6200EE",
                    mr: 1,
                  }}
                ></Box>
                <Typography sx={{ color: "#00000099", fontSize: "12px" }}>
                  Real Users Experience (CLS)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 3,
                    backgroundColor: "#FF9800",
                    mr: 1,
                  }}
                ></Box>
                <Typography sx={{ color: "#00000099", fontSize: "12px" }}>
                  CLS Target
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}></Grid>
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <Typography sx={{ fontSize: "18px", color: "#000000" }}>
            FCP (First Contentful Paint)
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#00000099" }}>
            in Seconds
          </Typography>
          <LineChartPro
            dataset={combinedDatasetFCP}
            series={[
              {
                type: "line",
                dataKey: "y",
                color: "#6200EE",
                curve: "linear",
              },
              {
                type: "line",
                dataKey: "z",
                color: "#FF9800",
                curve: "linear",
              },
            ]}
            xAxis={[{ scaleType: "point", dataKey: "x", disableLine: true }]}
            yAxis={[
              {
                disableLine: true,
                disableTicks: true,
                min: 0,
                max: 4,
                position: "right",
              },
            ]}
            height={350}
            margin={{ left: 32, right: 42, top: 25, bottom: 30 }}
            sx={{
              [`& .${lineElementClasses.root}`]: {
                strokeWidth: 2,
              },
              [`& .${markElementClasses.root}`]: {
                scale: "0.6",
                strokeWidth: 2,
                "&:hover": {
                  scale: "1.2",
                  strokeWidth: 5,
                  opacity: "0.6",
                },
              },
            }}
            leftAxis={null}
            rightAxis={{}}
          >
            <ChartsGrid horizontal />
            <CustomItemTooltip />
            <ChartsXAxis />
            <ChartsYAxis />
            <LinePlot />
            <MarkPlot />
            <ChartsLegend direction="row" />
          </LineChartPro>
          <Grid
            container
            spacing={0}
            direction="row"
            mb={2}
            pt={2}
            className="ratedTableWrap"
            sx={{ pl: 6 }}
          >
            <Grid item xs={4}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 3,
                    backgroundColor: "#6200EE",
                    mr: 1,
                  }}
                ></Box>
                <Typography sx={{ color: "#00000099", fontSize: "12px" }}>
                  Real Users Experience (FCP)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 3,
                    backgroundColor: "#FF9800",
                    mr: 1,
                  }}
                ></Box>
                <Typography sx={{ color: "#00000099", fontSize: "12px" }}>
                  FCP Target
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}></Grid>
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <Typography sx={{ fontSize: "18px", color: "#000000" }}>
            TTFB (Time to First Byte)
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#00000099" }}>
            in Seconds
          </Typography>
          <LineChartPro
            dataset={combinedDatasetTTFB}
            series={[
              {
                type: "line",
                dataKey: "y",
                color: "#6200EE",
                curve: "linear",
              },
              {
                type: "line",
                dataKey: "z",
                color: "#FF9800",
                curve: "linear",
              },
            ]}
            xAxis={[{ scaleType: "point", dataKey: "x", disableLine: true }]}
            height={350}
            yAxis={[
              {
                disableLine: true,
                disableTicks: true,
                min: 0,
                max: 2,
                position: "right",
              },
            ]}
            margin={{ left: 32, right: 32, top: 25, bottom: 30 }}
            sx={{
              [`& .${lineElementClasses.root}`]: {
                strokeWidth: 2,
              },
              [`& .${markElementClasses.root}`]: {
                scale: "0.6",
                strokeWidth: 2,
                "&:hover": {
                  scale: "1.2",
                  strokeWidth: 5,
                  opacity: "0.6",
                },
              },
            }}
            leftAxis={null}
            rightAxis={{}}
          >
            <ChartsGrid horizontal />
            <CustomItemTooltip />
            <ChartsXAxis />
            <ChartsYAxis />
            <LinePlot />
            <MarkPlot />
            <ChartsLegend direction="row" />
          </LineChartPro>
          <Grid
            container
            spacing={0}
            direction="row"
            mb={2}
            pt={2}
            className="ratedTableWrap"
            sx={{ pl: 6 }}
          >
            <Grid item xs={4}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 3,
                    backgroundColor: "#6200EE",
                    mr: 1,
                  }}
                ></Box>
                <Typography sx={{ color: "#00000099", fontSize: "12px" }}>
                  Real Users Experience (TTFB)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 3,
                    backgroundColor: "#FF9800",
                    mr: 1,
                  }}
                ></Box>
                <Typography sx={{ color: "#00000099", fontSize: "12px" }}>
                  TTFB Target
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}></Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default PerformanceDashboardRealUsersExperience;
